import { useState } from 'react';
import { Loader2, Mail, Lock, User as UserIcon, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

type AuthMode = 'signin' | 'signup' | 'forgot';

export function AuthPage() {
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'signup') {
        if (!fullName.trim()) {
          setError('Please enter your name');
          setLoading(false);
          return;
        }
        const { error } = await signUp(email, password, fullName);
        if (error) setError(error.message);
      } else if (mode === 'signin') {
        const { error } = await signIn(email, password);
        if (error) setError(error.message);
      } else if (mode === 'forgot') {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) setError(error.message);
        else setResetSent(true);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a3e54] flex flex-col items-center justify-center p-6">
      {/* Logo Section */}
      <div className="text-center mb-10 flex flex-col items-center gap-2 animate-fadeIn">
        <span className="text-6xl mb-2">⏳</span>
        <h1 className="text-5xl font-playfair font-bold text-[#feeddd] tracking-tight">
          Countr
        </h1>
      </div>

      {/* Main Card */}
      <div className="max-w-md w-full bg-[#feeddd] rounded-[2rem] shadow-2xl overflow-hidden animate-slideUp">
        {/* Tabs - Only show for Login/Signup */}
        {mode !== 'forgot' && (
          <div className="flex border-b border-[#1a3e54]/10">
            <button
              onClick={() => { setMode('signin'); setError(''); }}
              className={`flex-1 py-5 text-sm font-bold tracking-wider uppercase transition-all ${
                mode === 'signin'
                  ? 'text-[#1a3e54] border-b-4 border-[#9e142f] bg-white/30'
                  : 'text-[#1a3e54]/40 hover:text-[#1a3e54]/60'
              }`}
            >
              Log In
            </button>
            <button
              onClick={() => { setMode('signup'); setError(''); }}
              className={`flex-1 py-5 text-sm font-bold tracking-wider uppercase transition-all ${
                mode === 'signup'
                  ? 'text-[#1a3e54] border-b-4 border-[#9e142f] bg-white/30'
                  : 'text-[#1a3e54]/40 hover:text-[#1a3e54]/60'
              }`}
            >
              Sign Up
            </button>
          </div>
        )}

        {/* Forgot Password Header */}
        {mode === 'forgot' && (
          <div className="px-8 pt-8">
            <button 
              onClick={() => { setMode('signin'); setResetSent(false); setError(''); }}
              className="flex items-center gap-2 text-[#1a3e54]/40 hover:text-[#1a3e54] transition-colors text-xs font-bold uppercase tracking-wider"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Log In
            </button>
            <h2 className="text-2xl font-playfair font-bold text-[#1a3e54] mt-6">
              Reset Password
            </h2>
            <p className="text-sm text-[#1a3e54]/60 mt-2">
              Enter your email and we'll send you a link to get back into your account.
            </p>
          </div>
        )}

        <div className="p-8 sm:p-10">
          {resetSent ? (
            <div className="text-center py-4 animate-fadeIn">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-[#1a3e54] mb-2">Check your email</h3>
              <p className="text-sm text-[#1a3e54]/60 mb-8">
                We've sent a password reset link to <span className="font-bold text-[#1a3e54]">{email}</span>.
              </p>
              <button
                onClick={() => { setMode('signin'); setResetSent(false); }}
                className="w-full py-4 bg-[#9e142f] text-white font-bold rounded-2xl shadow-lg hover:bg-[#801026] transition-all"
              >
                Return to Log In
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {mode === 'signup' && (
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-[#1a3e54] uppercase tracking-[0.2em] ml-1">
                    Full Name
                  </label>
                  <div className="relative group">
                    <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1a3e54]/30 group-focus-within:text-[#9e142f] transition-colors" />
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 bg-white border-2 border-transparent rounded-2xl focus:ring-0 focus:border-[#9e142f] outline-none text-[#1a3e54] font-medium shadow-sm transition-all placeholder:text-[#1a3e54]/20"
                      placeholder="Jane Doe"
                      required
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-[10px] font-black text-[#1a3e54] uppercase tracking-[0.2em] ml-1">
                  Email Address
                </label>
                <div className="relative group">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1a3e54]/30 group-focus-within:text-[#9e142f] transition-colors" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 bg-white border-2 border-transparent rounded-2xl focus:ring-0 focus:border-[#9e142f] outline-none text-[#1a3e54] font-medium shadow-sm transition-all placeholder:text-[#1a3e54]/20"
                    placeholder="name@example.com"
                    required
                  />
                </div>
              </div>

              {mode !== 'forgot' && (
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-[#1a3e54] uppercase tracking-[0.2em] ml-1 flex justify-between items-center">
                    Password
                    {mode === 'signin' && (
                      <button 
                        type="button"
                        onClick={() => { setMode('forgot'); setError(''); }}
                        className="text-[#9e142f] font-bold hover:underline normal-case tracking-normal"
                      >
                        Forgot password?
                      </button>
                    )}
                  </label>
                  <div className="relative group">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1a3e54]/30 group-focus-within:text-[#9e142f] transition-colors" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-12 pr-12 py-4 bg-white border-2 border-transparent rounded-2xl focus:ring-0 focus:border-[#9e142f] outline-none text-[#1a3e54] font-medium shadow-sm transition-all placeholder:text-[#1a3e54]/20"
                      placeholder="••••••••"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#1a3e54]/30 hover:text-[#1a3e54]/60 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              )}

              {error && (
                <div className="p-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 text-xs font-semibold animate-shake">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-[#9e142f] hover:bg-[#801026] disabled:bg-gray-400 text-white text-base font-bold rounded-2xl shadow-xl hover:shadow-2xl transform active:scale-[0.98] transition-all flex items-center justify-center gap-3 mt-4"
              >
                {loading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  mode === 'signin' ? 'Log In' : mode === 'signup' ? 'Create Account' : 'Send Reset Link'
                )}
              </button>
            </form>
          )}

          {mode !== 'forgot' && !resetSent && (
            <p className="mt-10 text-center text-xs text-[#1a3e54]/40 font-medium">
              {mode === 'signin' ? (
                <>
                  New to Countr?{' '}
                  <button onClick={() => setMode('signup')} className="text-[#9e142f] font-bold hover:underline">
                    Sign up for free
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button onClick={() => setMode('signin')} className="text-[#9e142f] font-bold hover:underline">
                    Log in here
                  </button>
                </>
              )}
            </p>
          )}
        </div>
      </div>
      
      <p className="mt-8 text-[#feeddd]/40 text-[10px] font-bold uppercase tracking-widest">
        &copy; 2026 Countr &bull; Every moment counts
      </p>
    </div>
  );
}